/**
 * Tuner (F-PRAC-02): microphone → AnalyserNode → YIN at ~10 Hz on the main
 * thread (≈ 3–10 ms per analysis). The microphone is never connected to the
 * speakers. Candidate for an AudioWorklet if CPU becomes a problem.
 */
import { setAudioSessionType } from "../audio/session";
import { errorMessage } from "../core/errors";
import { detectPitch, noteFromFrequency, type NoteReading } from "../learning/pitch";
import type { EngineController } from "./controller";
import { Store } from "./store";

export type TunerStatus = "idle" | "starting" | "listening" | "denied" | "unsupported" | "error";

export interface TunerState {
  status: TunerStatus;
  message: string | null;
  reading: NoteReading | null;
  a4Hz: number;
}

export interface TunerDeps {
  mediaDevices?: Pick<MediaDevices, "getUserMedia">;
  setInterval?: typeof setInterval;
  clearInterval?: typeof clearInterval;
}

export class TunerService {
  readonly state = new Store<TunerState>({ status: "idle", message: null, reading: null, a4Hz: 440 });
  private stream: MediaStream | null = null;
  private source: MediaStreamAudioSourceNode | null = null;
  private analyser: AnalyserNode | null = null;
  private timer: ReturnType<typeof setInterval> | null = null;
  private buf: Float32Array<ArrayBuffer> | null = null;
  private lastHeard = 0;

  constructor(private c: EngineController, private deps: TunerDeps = {}) {}

  private get media(): Pick<MediaDevices, "getUserMedia"> | undefined {
    return this.deps.mediaDevices ?? globalThis.navigator?.mediaDevices;
  }

  async start(): Promise<void> {
    if (this.stream) return;
    const media = this.media;
    if (!media?.getUserMedia) {
      this.patch({ status: "unsupported", message: "This browser does not allow microphone access (getUserMedia unavailable or insecure context)." });
      return;
    }
    this.patch({ status: "starting", message: null });
    if (!(await this.c.startAudio()) || !this.c.audioContext) {
      this.patch({ status: "error", message: "Audio could not start." });
      return;
    }
    // iOS needs play-and-record to capture; output may move to the earpiece until stop().
    setAudioSessionType("play-and-record");
    this.c.micActive = true;
    try {
      // Raw signal: echo cancellation / noise suppression / AGC distort pitch.
      this.stream = await media.getUserMedia({ audio: { echoCancellation: false, noiseSuppression: false, autoGainControl: false } });
    } catch (err) {
      this.releaseSession();
      const name = err instanceof Error ? err.name : "";
      this.patch(name === "NotAllowedError" || name === "SecurityError"
        ? { status: "denied", message: "Microphone permission was denied. Allow the microphone for this site to use the tuner." }
        : { status: "error", message: `Microphone unavailable: ${errorMessage(err)}` });
      return;
    }
    const ctx = this.c.audioContext;
    this.source = ctx.createMediaStreamSource(this.stream);
    this.analyser = ctx.createAnalyser();
    this.analyser.fftSize = 4096;
    this.buf = new Float32Array(this.analyser.fftSize);
    this.source.connect(this.analyser); // deliberately not connected to the destination (no feedback)
    const every = this.deps.setInterval ?? setInterval;
    this.timer = every(() => this.analyse(), 100);
    this.patch({ status: "listening", message: null, reading: null });
  }

  /** One analysis step (public for tests). */
  analyse(nowMs = Date.now()): void {
    if (!this.analyser || !this.buf || !this.c.audioContext) return;
    this.analyser.getFloatTimeDomainData(this.buf);
    const r = detectPitch(this.buf, this.c.audioContext.sampleRate, { minHz: 38, maxHz: 1600 });
    if (r && r.clarity > 0.85) {
      this.lastHeard = nowMs;
      this.patch({ reading: noteFromFrequency(r.frequency, this.state.get().a4Hz) });
    } else if (this.state.get().reading && nowMs - this.lastHeard > 1500) {
      this.patch({ reading: null }); // hold the last note briefly, then clear
    }
  }

  setA4(hz: number): void {
    this.patch({ a4Hz: Math.max(415, Math.min(466, Math.round(hz))) });
  }

  stop(): void {
    if (this.timer !== null) (this.deps.clearInterval ?? clearInterval)(this.timer);
    this.timer = null;
    this.source?.disconnect();
    this.analyser?.disconnect();
    this.stream?.getTracks().forEach((t) => t.stop());
    if (this.stream) this.releaseSession();
    this.stream = this.source = this.analyser = null;
    if (this.state.get().status === "listening") this.patch({ status: "idle", reading: null });
  }

  private releaseSession(): void {
    this.c.micActive = false;
    setAudioSessionType("playback"); // back to the loudspeaker
  }

  private patch(p: Partial<TunerState>): void {
    this.state.set({ ...this.state.get(), ...p });
  }
}
